import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSpring } from 'react-spring';
import {
  DropDownWrapper,
  DropDownInput,
  DropDownSelect,
  ListOfOptions,
  TopicOption,
  TitlePlaceholder,
  SelectedPlaceholder,
  ArrowIcons,
  Checkbox,
  HiddenInput,
  Title,
  DropDown,
  DropdownLabel,
  SubLabel,
} from './DropDownSelector.styles';
import useAnimations from '../../../hooks/useAnimations';
import { selectInputs, setUserInputs } from '../../../redux/inputsReducer';
import { digitalLeadsFormInput } from '../../../utils/analyticsEventListeners';
import ArrowUpIcon from '../../../assets/icons/ArrowUpIcon';
import ArrowDownIcon from '../../../assets/icons/ArrowDownIcon';
import { COLORS } from '../../../styles/variables';
import CheckMarkIcon from '../../../assets/icons/CheckMarkIcon';
import useClickOutside from '../../../hooks/useClickOutside';
import { CheckBoxInputProps } from '../../../types';
import { Form } from '../../../types/analytics';
import { Note } from '../TextInput/TextInput.styles';
import { OptionalNote } from '../CheckboxInput/CheckBoxInput.styles';
import { FEATURE_APP_DETAILS } from '../../../constants';
import { convertToPromotionsType } from '../../../functions/convertToPromotionsType';
import { selectDealerAndVehicleData } from '../../../redux/dealerAndVehicleReducer';
import { selectForm, setForm } from '../../../redux/formReducer';
import { selectAppConfig } from '../../../redux/appConfigReducer';
import { getVehicleForPromotions } from '../../../functions/getVehicleForPromotions';

interface DropDownSelectorProps {
  options: CheckBoxInputProps;
  title?: string;
  inputLabel: string;
  inputKey: string;
  inputNote?: string;
  isOptional?: boolean;
  dropdownHeight: number;
}

const DropDownSelector: React.FC<DropDownSelectorProps> = ({
  options,
  title,
  inputLabel,
  inputKey,
  inputNote,
  isOptional,
  dropdownHeight,
}) => {
  const dispatch = useDispatch();
  const { vehicle } = useSelector(selectDealerAndVehicleData);
  const contactForm = useSelector(selectForm);
  const inputs = useSelector(selectInputs);
  const inputValue = inputs[inputKey];
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(
    isDropdownOpen,
    dropdownHeight,
  );

  const { offers } = useSelector(selectAppConfig);
  const promotion = convertToPromotionsType(offers!);
  const isAnyTopciSelected = !!inputValue;

  const inputAnimation = useSpring({
    to: {
      top: isAnyTopciSelected ? -22 : 4,
      fontSize: isAnyTopciSelected ? '12px' : '16px',
    },
  });

  let vehicleData = vehicle;

  if (!vehicle && offers) {
    vehicleData = getVehicleForPromotions(offers);
  }

  const onHandleChange = useCallback(
    (value: string) => {
      const inputTouched = {
        inputTitle: inputKey,
        inputValue: value,
        action: 'select',
        inputType: 'dropdown',
      };

      const updatedForm: Form = {
        ...contactForm,
        ...{ formData: { ...contactForm.formData, ...{ [inputKey]: value } } },
      };

      dispatch(setForm(updatedForm));
      dispatch(setUserInputs({ inputName: inputKey, inputValue: value }));
      digitalLeadsFormInput(updatedForm, vehicleData, FEATURE_APP_DETAILS, promotion, inputTouched);

      setIsDropdownOpen(false);
    },
    [inputKey, contactForm, dispatch, vehicleData, promotion],
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent, value: string) => {
      if (e.key !== ' ') return;
      e.preventDefault();
      onHandleChange(value);
    },
    [onHandleChange],
  );

  const dropdownRef = useRef(null);

  useClickOutside(
    dropdownRef,
    useCallback(() => {
      setIsDropdownOpen(false);
    }, [setIsDropdownOpen]),
  );

  const listOfOptions = Object.keys(options);

  return (
    <DropDownWrapper ref={dropdownRef} isOptional={isOptional} data-testid="dropdown-wrapper">
      {title && <Title>{title}</Title>}
      <DropDownInput
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        data-testid="dropdown-trigger"
      >
        <TitlePlaceholder style={inputAnimation}>
          {inputLabel}
          {isOptional && <OptionalNote>(optional)</OptionalNote>}
        </TitlePlaceholder>
        {isAnyTopciSelected && (
          <SelectedPlaceholder>
            {options[inputValue].label}

            <SubLabel>{options[inputValue].subLabel}</SubLabel>
          </SelectedPlaceholder>
        )}
        <ArrowIcons>
          {isDropdownOpen ? (
            <ArrowUpIcon color={COLORS.black} />
          ) : (
            <ArrowDownIcon color={COLORS.black} />
          )}
        </ArrowIcons>
      </DropDownInput>
      {dropdownAnimation(
        (props, item) =>
          item && (
            <DropDownSelect style={props}>
              <DropDown style={filtersAccordianAnimation} data-testid="dropdown-selector">
                <ListOfOptions>
                  {listOfOptions.map((option) => {
                    const { value, label, key } = options[option];
                    const sublabel = options[option]?.subLabel;
                    const checked = inputValue === value;
                    return (
                      <TopicOption key={key} onClick={() => onHandleChange(value)}>
                        <Checkbox
                          tabIndex={0}
                          onClick={() => onHandleChange(value)}
                          role="checkbox"
                          // aria-label={ariaLabel}
                          aria-checked={checked}
                          onKeyDown={(e) => handleKeyPress(e, value)}
                          checked={checked}
                          data-testid="dropdown-option-checkbox"
                        >
                          {checked && <CheckMarkIcon />}
                        </Checkbox>
                        <HiddenInput
                          // id={inputName}
                          type="checkbox"
                          onChange={() => onHandleChange(value)}
                          checked={checked}
                          name={value}
                        />
                        <DropdownLabel>
                          {label}
                          {sublabel && <SubLabel>{sublabel}</SubLabel>}
                        </DropdownLabel>
                      </TopicOption>
                    );
                  })}
                </ListOfOptions>
              </DropDown>
            </DropDownSelect>
          ),
      )}
      <Note>{inputNote}</Note>
    </DropDownWrapper>
  );
};

export default DropDownSelector;
